import { useApolloClient } from '@provision/graphql-client';
import { useEffect, useState } from 'react';
import { gql } from 'src/gql';

export enum TargetLookupStatus {
  Idle = 'idle',
  Loading = 'loading',
  Found = 'found',
  Missing = 'missing',
}

export interface TargetMessageLookup {
  status: TargetLookupStatus;
  /**
   * The timeline message that brings the target on screen: the target itself, or for a reply
   * the note it belongs to. Replies are no timeline items: they render inside that note.
   */
  timelineMessageId: string | null;
  /** When the timeline message was created. */
  createdAt: string | null;
}

interface LookupResult extends TargetMessageLookup {
  targetMessageId: string;
}

const IDLE_LOOKUP: TargetMessageLookup = {
  status: TargetLookupStatus.Idle,
  timelineMessageId: null,
  createdAt: null,
};
const LOADING_LOOKUP: TargetMessageLookup = {
  status: TargetLookupStatus.Loading,
  timelineMessageId: null,
  createdAt: null,
};

const DeepLinkTargetMessageQuery = gql(`
  query DeepLinkTargetMessageQuery($params: QueryParamsWithSearch) {
    getConversationMessage(queryParams: $params) {
      records {
        id
        conversationMessageId
        parentConversationMessageId
        createdAt
      }
    }
  }
`);

/*
  Asks the server whether the target is a live message of this conversation (a deleted one,
  like a deleted reply, reads as not found), which timeline
  message brings it on screen, and when that one was created — the paging that brings it into
  the thread stops once the loaded window is older than that.
*/
export function useTargetMessageLookup(
  conversationId: string,
  targetMessageId: string | null,
): TargetMessageLookup {
  const client = useApolloClient();
  const [result, setResult] = useState<LookupResult | null>(null);

  useEffect(() => {
    if (!targetMessageId) return;
    let ignore = false;

    const fetchMessage = async (conversationMessageId: string) => {
      const { data } = await client.query({
        query: DeepLinkTargetMessageQuery,
        variables: {
          params: {
            filters: [
              { attribute: 'conversationId', operator: '=', value: conversationId },
              {
                attribute: 'conversationMessageId',
                operator: '=',
                value: conversationMessageId,
              },
            ],
            offset: 0,
            limit: 1,
          },
        },
        // a one-record page written into the cache could merge into the thread's own list
        fetchPolicy: 'no-cache',
      });
      return data?.getConversationMessage?.records?.[0] ?? null;
    };

    const lookUp = async () => {
      let lookup: TargetMessageLookup = {
        status: TargetLookupStatus.Missing,
        timelineMessageId: null,
        createdAt: null,
      };
      try {
        const target = await fetchMessage(targetMessageId);
        const timelineMessage = target?.parentConversationMessageId
          ? await fetchMessage(target.parentConversationMessageId)
          : target;
        if (timelineMessage) {
          lookup = {
            status: TargetLookupStatus.Found,
            timelineMessageId: timelineMessage.conversationMessageId,
            createdAt: timelineMessage.createdAt,
          };
        }
      } catch {
        // a malformed id is rejected by the API rather than returning no rows
      }
      if (!ignore) setResult({ targetMessageId, ...lookup });
    };
    void lookUp();

    return () => {
      ignore = true;
    };
  }, [client, conversationId, targetMessageId]);

  if (!targetMessageId) return IDLE_LOOKUP;
  if (result?.targetMessageId !== targetMessageId) return LOADING_LOOKUP;
  return result;
}
